/**
 * googleAuth.js — Feb 2026
 *
 * Shared helper for the direct-Google OAuth flow. Fetches the public
 * `client_id` from `/api/auth/google/config` at runtime so we don't
 * need to bake it into the frontend build with a REACT_APP_* env var
 * (which would require a full redeploy every time the credential rotates).
 *
 * If the backend returns `enabled: false` (i.e. GOOGLE_CLIENT_ID /
 * GOOGLE_CLIENT_SECRET aren't set), callers fall back to the legacy
 * Emergent-hosted OAuth handoff so sign-in keeps working.
 *
 * The config is cached in-module for the lifetime of the tab — Google
 * client IDs don't rotate mid-session and this avoids re-hitting the
 * backend on every button render.
 */
import { api } from '@/contexts/AuthContext';

let cache = null;         // { enabled: bool, client_id: string|null }
let inFlight = null;      // dedupes concurrent fetches

/** Resolve the direct-Google config, cached for the tab. */
export async function getGoogleAuthConfig() {
  if (cache) return cache;
  if (inFlight) return inFlight;
  inFlight = api.get('/auth/google/config')
    .then((r) => {
      cache = {
        enabled:   !!r.data?.enabled,
        client_id: r.data?.client_id || null,
      };
      return cache;
    })
    .catch(() => {
      // Treat any error as "not configured" — Emergent fallback keeps
      // sign-in alive even if the config probe fails.
      cache = { enabled: false, client_id: null };
      return cache;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Trigger the Google Sign-In redirect. Uses direct flow when enabled,
 *  Emergent-hosted flow otherwise. Always returns void — the browser
 *  navigates away. */
export async function launchGoogleSignIn() {
  const cfg = await getGoogleAuthConfig();
  if (cfg.enabled && cfg.client_id) {
    const redirectUri = window.location.origin + '/auth/google/callback';
    // CSRF: random state stored in sessionStorage; the callback page
    // verifies the round-trip and clears it after use.
    const state = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    sessionStorage.setItem('google_direct_state', state);
    const params = new URLSearchParams({
      client_id:     cfg.client_id,
      redirect_uri:  redirectUri,
      response_type: 'code',
      scope:         'openid email profile',
      access_type:   'online',
      include_granted_scopes: 'true',
      prompt:        'select_account',
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    return;
  }
  // Legacy Emergent flow — remains active until credentials are set.
  const redirectUrl = window.location.origin + '/discover';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
